import { useState } from "react";
import {
  Grid,
  TextField,
  Button,
  Typography,
  CircularProgress,
} from "@mui/material";
import AlertBar from "../utils/AlertBar";
import { Link, useNavigate } from "react-router-dom";
import { API_URL } from "../../config/environment";
import { useTranslation } from "react-i18next";
import secureLocalStorage from "react-secure-storage";
import { useTitle } from "../utils/useTitle";
import { useAnalyticsEventTracker } from "../utils/useAnalyticsEventTracker";

const SignIn = () => {
  useTitle("Sign In");
  const eventTracker = useAnalyticsEventTracker();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);

  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });
  const [errors, setErrors] = useState({
    email: false,
    password: false,
  });
  const [errorMessage, setErrorMessage] = useState("");

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
    // Clear the error for the current field when the user starts typing
    setErrors((prevErrors) => ({
      ...prevErrors,
      [name]: false,
    }));
  };

  const handleFormSubmit = async () => {
    setIsLoading(true);

    // Validate form fields
    const formErrors = {};
    Object?.keys(formData).forEach((key) => {
      if (!formData[key]) {
        formErrors[key] = true;
      }
    });

    if (Object?.keys(formErrors).length > 0) {
      // If there are errors, set the state to show errors
      setErrors(formErrors);
      setErrorMessage(t("auth.alertMsg.error1"));
      setIsLoading(false);
      return;
    }

    // Placeholder for the API request logic
    const url = `${API_URL}/auth/signin`;

    try {
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (data.status === "success") {
        const user = JSON.stringify(data.user);
        sessionStorage.setItem("authToken", data.authToken);
        secureLocalStorage.setItem("userInfo", user);
        secureLocalStorage.setItem("ingredientsMetrics", []);
        secureLocalStorage.setItem("ingredients", []);

        // Reset form data and errors after successful submission or error
        setFormData({
          email: "",
          password: "",
        });

        navigate("/dashboard");
        eventTracker("signin", {
          user_id: data.user.userId,
        });
      } else {
        setErrors({ error: true });
        setErrorMessage(data.message);
      }
    } catch (error) {
      console.error("Error:", error);
      setErrors({ error: true });
      setErrorMessage(t("auth.alertMsg.error3"));
    } finally {
      // Reset form data and errors after successful submission or error
      // setFormData({
      //   email: "",
      //   password: "",
      // });
      setIsLoading(false);
    }
  };

  return (
    <>
      <Grid container>
        {/* Left Side (8 columns) */}

        <Grid item xs={6} container justify="center" alignItems="center">
          <Grid
            container
            direction="column"
            spacing={3}
            style={{ maxWidth: "800px", padding: "20px", marginLeft: "70px" }}
          >
            <Grid item>
              <h1>{t("auth.header.signIn")}</h1>
              <TextField
                label={t("auth.email")}
                type="email"
                variant="outlined"
                fullWidth
                required
                value={formData.email}
                onChange={handleInputChange}
                name="email"
                error={errors.email}
                helperText={errors.email && t("auth.requiredMsg.email")}
              />
            </Grid>
            <Grid item>
              <TextField
                label={t("auth.password")}
                type="password"
                variant="outlined"
                fullWidth
                required
                value={formData.password}
                onChange={handleInputChange}
                name="password"
                error={errors.password}
                helperText={errors.password && t("auth.requiredMsg.password")}
              />
            </Grid>
            <Grid item>
              <Button
                variant="contained"
                color="success"
                fullWidth
                size="large"
                style={{ height: "55px" }}
                onClick={handleFormSubmit}
              >
                {isLoading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  t("auth.header.signIn")
                )}
              </Button>
            </Grid>

            <Grid item>
              <Typography variant="subtitle1" gutterBottom>
                {t("auth.msg.forgotPass")}{" "}
                <Link to="/forgot-password">
                  {" "}
                  {t("auth.msg.forgotPassLinkText")}
                </Link>{" "}
                {t("auth.msg.forgotPassEnd")}
              </Typography>
              <Typography variant="subtitle1" gutterBottom>
                {t("auth.msg.signUp")}
                <Link to="/signup">{t("auth.header.signUp")}</Link>{" "}
              </Typography>
            </Grid>
          </Grid>
        </Grid>

        {/* Right Side (6 columns) */}
        <Grid item xs={6} style={{ overflow: "hidden" }}>
          <div style={{ width: "100%", height: "100vh" }}>
            <img
              src="assets/images/banner1.jpg"
              alt="Signin"
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
              }}
            />
          </div>
        </Grid>
      </Grid>

      <AlertBar
        open={Object.values(errors).some((error) => error)}
        message={errorMessage}
        severity={"error"}
        onClose={() => setErrors({})}
      />
    </>
  );
};

export default SignIn;
